import {
  LitElement,
  html,
  css,
  classMap,
  ref,
  createRef,
  map,
  range,
  unsafeHTML,
  styles,
  paths,
  utils,
  t,
} from '@hh/global.js'
const { throttle } = utils

class HHImageCarousel extends LitElement {
  static properties = {
    current: {
      state: true,
      reflect: true,
      type: Number,
    },
    _total: {
      state: true,
    },
    speed: {
      reflect: true,
      type: Number,
      converter: {
        fromAttribute: (value) => {
          return Number(value * 1000)
        },
      },
    },
    playing: {
      reflect: true,
      type: Boolean,
    },
    userPause: {
      type: Boolean,
    },
    autoplay: {
      state: true,
    },
    toggle: {
      reflect: true,
      type: Boolean,
    },
    pagination: {
      reflect: true,
      type: Boolean,
    },
    counter: {
      reflect: true,
      type: Boolean,
    },
    arrows: {
      reflect: true,
      type: Boolean,
    },
    intersectionObserver: {
      reflect: true,
      type: Boolean,
      attribute: 'intersection-observer',
    },
    slideCount: {
      type: Number,
      attribute: 'slide-count',
    },
  }

  $slides = createRef()
  $next = createRef()
  $prev = createRef()
  $togglePlay = createRef()

  constructor() {
    super()

    this._total = 0
    this.current = 1
    this.playing = false
    this.userPause = false
    this.highlightCompleted = false

    this.touchstartX = 0
    this.touchendX = 0
    this.swipeThreshold = 50
    this.activeMediaType = ''

    this.throttledUpdateUI = throttle(this.updateUI.bind(this), 100)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    this.$slides?.value?.addEventListener('scroll', this.throttledUpdateUI, {
      passive: true,
    })

    if (
      this.intersectionObserver &&
      this.speed &&
      !this.reducedMotion.matches
    ) {
      this.setPlayPauseIntersectionObserver()
    } else if (this.speed && !this.reducedMotion.matches) {
      this.setAutoplay()
    } else {
      this.autoplay = false
      this.userPause = true
    }

    this.emitMediaSlideChange()
    this.setSlideVisibility()
  }

  updated(changedProperties) {
    if (changedProperties.has('current')) {
      this.setSlideVisibility()
    }
  }

  updateUI() {
    if (!this._total) return

    this.current =
      Math.round(this.$slides?.value?.scrollLeft / this.items[0].clientWidth) +
      1
    this.current === 1
      ? this.$prev?.value?.setAttribute('disabled', true)
      : this.$prev?.value?.removeAttribute('disabled')

    this.current === this._total
      ? this.$next?.value?.setAttribute('disabled', true)
      : this.$next?.value?.removeAttribute('disabled')

    this.emitMediaSlideChange()
  }

  emitMediaSlideChange() {
    if (!this.items?.length) return

    const activeItem = this.items[this.current - 1]
    const mediaType = activeItem?.dataset?.mediaType || ''
    if (this.activeMediaType === mediaType) return

    this.activeMediaType = mediaType
    this.dispatchEvent(
      new CustomEvent('mediaSlideChange', {
        bubbles: true,
        composed: true,
        detail: {
          current: this.current,
          mediaType,
        },
      })
    )
  }

  // Remove an element from the tab order, remembering its original tabindex
  // (or its absence) so it can be restored exactly when the slide reactivates.
  // Hosts like <model-viewer> are only focusable via tabindex, so blindly
  // removing the attribute would make them permanently unreachable.
  disableTabbing(el) {
    if (!el.hasAttribute('data-hh-prev-tabindex')) {
      el.setAttribute(
        'data-hh-prev-tabindex',
        el.getAttribute('tabindex') ?? ''
      )
    }
    el.setAttribute('tabindex', '-1')
  }

  restoreTabbing(el) {
    if (!el.hasAttribute('data-hh-prev-tabindex')) return

    const prev = el.getAttribute('data-hh-prev-tabindex')
    if (prev === '') {
      el.removeAttribute('tabindex')
    } else {
      el.setAttribute('tabindex', prev)
    }
    el.removeAttribute('data-hh-prev-tabindex')
  }

  setFocusableState(root, isActive) {
    // [data-hh-prev-tabindex] keeps previously disabled elements matchable
    // even though their tabindex is currently -1.
    const focusables = root.querySelectorAll(
      'a, button, input, select, textarea, hh-button, [tabindex], [data-hh-prev-tabindex]'
    )

    focusables.forEach((el) => {
      if (el === root) return

      if (isActive) {
        this.restoreTabbing(el)
      } else {
        this.disableTabbing(el)
      }

      const shadowFocusables = el.shadowRoot?.querySelectorAll('a, button')
      shadowFocusables?.forEach((inner) => {
        if (isActive) {
          this.restoreTabbing(inner)
        } else {
          this.disableTabbing(inner)
        }
      })
    })
  }

  // Hide inactive slides from assistive tech and remove them from the tab
  // order so keyboard users reach the arrows instead of every slide.
  setSlideVisibility() {
    if (!this.items?.length) return

    this.items.forEach((item, idx) => {
      const isActive = idx === this.current - 1

      item.setAttribute('aria-hidden', isActive ? 'false' : 'true')

      if (isActive) {
        item.removeAttribute('tabindex')
      } else {
        item.setAttribute('tabindex', '-1')
      }

      this.setFocusableState(item, isActive)
    })
  }

  // Autoplay methods
  setAutoplay() {
    if (!this.speed) return

    clearInterval(this.autoplay)
    this.autoplay = setInterval(this.rotateSlides.bind(this), this.speed)
    this.play()
  }

  stopAutoplay() {
    clearInterval(this.autoplay)
    this.pause()
  }

  toggleAutoplay() {
    if (this.playing) {
      this.userPause = true
      this.stopAutoplay()
    } else {
      this.userPause = false
      this.setAutoplay()
    }
  }

  play() {
    this.container?.setAttribute('aria-live', 'off')
    this.playing = true
  }

  pause() {
    this.container?.setAttribute('aria-live', 'polite')
    this.playing = false
  }

  rotateSlides() {
    const slideScrollPosition =
      this.current === this._total
        ? 0
        : this.$slides?.value?.scrollLeft + this.items[0].clientWidth
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })

    if (this.current !== this._total) {
      const nextSlideImage = this.items[this.current].querySelector('img')
      this.eagerLoadImage(nextSlideImage)
    }
  }

  setPlayPauseIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) =>
        entry.isIntersecting ? this.onFocusOut() : this.onFocusIn()
      )
    })
    observer.observe(this.$slides?.value)
  }

  // Early load image for next slide
  eagerLoadImage(image) {
    if (!image) return
    image.setAttribute('loading', 'eager')
  }

  // Move to next or previous slide
  move(e) {
    e.preventDefault()
    e.stopPropagation()
    this.userPause = true
    this.stopAutoplay()
    const slideScrollPosition =
      e.currentTarget.name === 'next'
        ? this.$slides?.value?.scrollLeft + this.items[0].clientWidth
        : this.$slides?.value?.scrollLeft - this.items[0].clientWidth
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  // Move to specific slide
  moveTo(slide, behaviour = 'smooth') {
    const slideScrollPosition = this.items[slide].offsetLeft
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
      behavior: behaviour,
    })

    const nextSlideImage = this.items[slide].querySelector('img')
    this.eagerLoadImage(nextSlideImage)
  }

  // Event handlers
  onFocusIn() {
    if (this.userPause || this.reducedMotion.matches) return
    this.stopAutoplay()
  }

  onFocusOut() {
    if (this.userPause || this.reducedMotion.matches) return
    this.setAutoplay()
  }

  onPaginationClick(e, slideIndex) {
    e.preventDefault()
    this.userPause = true
    this.stopAutoplay()
    this.moveTo(slideIndex)
  }

  onTouchStart(e) {
    this.touchstartX = e.changedTouches[0].screenX
  }

  onTouchEnd(e) {
    this.touchendX = e.changedTouches[0].screenX
    this.checkSwipe()
  }

  checkSwipe() {
    if (
      this.touchendX < this.touchstartX - this.swipeThreshold ||
      this.touchendX > this.touchstartX + this.swipeThreshold
    ) {
      this.userPause = true
      this.stopAutoplay()
    }
  }

  renderArrows() {
    if (!this.arrows) return ''

    return html`
      <div class="arrows">
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          class="arrow arrow--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide')}"
          ?disabled="${this.current == 1}"
        >
          ${unsafeHTML(paths.arrow)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          class="arrow arrow--next"
          name="next"
          aria-label="${t('accessibility.next_slide')}"
          ?disabled="${this.current == this._total}"
        >
          ${unsafeHTML(paths.arrow)}
        </button>
      </div>
    `
  }

  render() {
    return html`<!-- @ts-ignore no-incompatible-type-binding -->
      ${/* Arrows first so keyboard users reach controls before slide content */ ''}
      ${this.renderArrows()}
      <div
        class="slides"
        ${ref(this.$slides)}
        @touchstart="${this.onTouchStart}"
        @touchend="${this.onTouchEnd}"
        @mouseenter="${this.onFocusIn}"
        @mouseleave="${this.onFocusOut}"
        @focusin="${this.onFocusIn}"
        @focusout="${this.onFocusOut}"
      >
        <slot name="item"></slot>
      </div>
      <div class="controls">
        ${/* Pagination */ ''}
        ${(this.pagination &&
        html`
            <div
              class="${classMap({
          pagination: true,
          hidden: this.slideCount === 1,
        })}"
            >
              ${map(range(this.slideCount), (idx) => {
          return html`
                  <button
                    class="pagination-button"
                    aria-label="${t(
            'sections.image_carousel.go_to_slide'
          )} ${idx + 1}"
                    aria-current="${this.current === idx + 1}"
                    @click="${(e) => this.onPaginationClick(e, idx)}"
                  ></button>
                `
        })}
            </div>
          `) ||
      ''}
        ${/* Play/pause button */ ''}
        ${(this.toggle &&
        html`
            <button
              class="${classMap({
          toggle: true,
          hidden: !this.speed || this.slideCount === 1,
        })}"
              @click=${this.toggleAutoplay}
              aria-label=${this.playing
            ? t('sections.image_carousel.pause')
            : t('sections.image_carousel.play')}
            >
              ${this.playing
            ? html`${unsafeHTML(paths.pause)}`
            : html`${unsafeHTML(paths.play)}`}
            </button>
          `) ||
      ''}
        ${/* Counter */ ''}
        ${(this.counter &&
        html`
            <div class="counter">
              <span class="counter--current">${this.current}</span>
              <span aria-hidden="true"> / </span>
              <span class="visually-hidden">${t('accessibility.of')}</span>
              <span class="counter--total"
                >${this._total ? this._total : ''}</span
              >
            </div>
          `) ||
      ''}
      </div>`
  }

  static styles = [
    styles.visuallyHidden,
    styles.hidden,
    css`
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }

      :host {
        position: relative;
        width: 100%;
        max-width: 100vw;
      }

      .slides {
        display: flex;
        flex-wrap: nowrap;
        align-items: var(--hh-align-items, center);
        position: relative;
        z-index: 0;
        width: 100%;
        height: var(--hh-carousel-height, auto);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
      }

      .slides::-webkit-scrollbar {
        display: none;
      }

      .controls {
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1;
        width: 100%;
        column-gap: 1rem;
        transition: opacity var(--hh-duration-default);
      }

      .pagination {
        display: flex;
        gap: 0.125rem;
        height: 2px;
        justify-content: center;
        width: 100%;
      }

      .pagination-button {
        width: 100%;
        height: var(--hh-pagination-button-size, 0.125rem);
        background-color: var(--hh-color-neutral);
        border: none;
        cursor: pointer;
        padding: 0;
      }

      .pagination-button[aria-current='true'] {
        background-color: var(--hh-color-brand-primary);
      }

      .toggle {
        align-items: center;
        aspect-ratio: 1;
        background: rgba(0, 0, 0, 0.2);
        border: none;
        border-radius: 100%;
        bottom: var(--hh-space-sm);
        cursor: pointer;
        display: flex;
        justify-content: center;
        padding: 0;
        position: absolute;
        right: var(--hh-space-sm);
        transition: opacity var(--hh-duration-default);
        width: max(34px, 2vw);
        height: max(34px, 2vw);
        z-index: 1;
      }

      .toggle svg {
        color: var(--hh-color-reverse);
        fill: var(--hh-color-reverse);
        width: 35%;
      }

      .counter {
        display: flex;
        column-gap: 0.5rem;
      }

      .arrows {
        display: flex;
        justify-content: space-between;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 100%;
        padding: 0 var(--hh-space-xs);
        pointer-events: none;
        z-index: 1;
      }

      .arrow {
        align-items: center;
        background: rgba(0, 0, 0, 0.2);
        border: none;
        border-radius: 50%;
        color: var(--hh-color-reverse);
        cursor: pointer;
        display: flex;
        height: var(--hh-arrow-button-size, 2.4rem);
        justify-content: center;
        padding: var(--hh-space-xs);
        width: var(--hh-arrow-button-size, 2.4rem);
        transition: opacity var(--hh-duration-default);
        pointer-events: auto;
      }

      .arrow svg {
        width: 100%;
      }

      .arrow--prev {
        transform: rotate(180deg);
      }

      .arrow[disabled] {
        cursor: auto;
        opacity: 0;
      }

      :host([location='product-card']) .controls {
        opacity: 1;
      }

      :host([location='product-card']) .arrows {
        display: none;
      }

      @media screen and (min-width: 750px) {
        :host([location='product-card']) .controls {
          opacity: 0;
        }

        :host([location='product-card']) .arrows {
          display: flex;
          padding: 0 var(--hh-space-xs);
        }

        :host([location='product-card']) .pagination {
          display: none;
        }

        :host([location='product-card']:hover) .controls,
        :host([location='product-card']:focus-within) .controls {
          opacity: 1;
        }
      }

      :host([location='hero-banner']) .toggle {
        position: absolute;
        right: 10px;
        top: calc(65vh - 45px);
      }

      @media screen and (min-width: 750px) {
        :host([location='hero-banner']) .toggle {
          opacity: 0;
          transition: opacity var(--hh-duration-default);
        }

        :host([location='hero-banner']:hover) .toggle {
          opacity: 1;
        }
      }

      :host([location='pdp']) .controls {
        opacity: 1;
      }

      @media screen and (min-width: 750px) {
        :host([location='pdp'][ctx='main']) .arrows {
          opacity: 0;
          padding: 0 var(--hh-space-sm);
          transition: opacity var(--hh-duration-default);
        }

        :host([location='pdp']:hover) .arrows {
          opacity: 1;
        }

        :host([location='pdp'][ctx='main']) .slides {
          height: 100%;
        }
      }

      :host([ctx='zoom']) .arrows {
        padding: 0 var(--hh-space-sm);
      }

      @media screen and (max-width: 749px) {
        :host([ctx='zoom']) .pagination {
          bottom: 0;
          position: fixed;
        }
      }

      :host([ctx='zoom']) .counter {
        bottom: 16px;
        position: fixed;
      }

      @media screen and (min-width: 750px) {
        :host([ctx='zoom']) .counter {
          display: none;
        }
      }
    `,
  ]
}

customElements.define('hh-image-carousel', HHImageCarousel)
